import { Plural, useLingui } from "@lingui/react/macro";
import type { Bot, Group, ThreadSnapshot } from "@rakazo/contracts";
import { Badge, BotAvatar, Button, GroupAvatar } from "@rakazo/ui-web";
import { Menu, Monitor, Moon, PanelRight, Sun, Users } from "lucide-react";
import { useState } from "react";
import { IconTooltip } from "../../components/IconTooltip";
import { chatHeaderState } from "../../lib/roster-status";
import type { ResolvedAppearance } from "../../lib/ui-appearance";
import { setUiAppearance } from "../../lib/ui-appearance";

function currentAppearance(): ResolvedAppearance {
  return document.documentElement.dataset.theme === "light" ? "light" : "dark";
}

/** Flips between light and dark in place; Settings still offers following the system. */
export function AppearanceToggle() {
  const { t } = useLingui();
  const [appearance, setAppearance] = useState(currentAppearance);
  const label = appearance === "light" ? t`Light` : t`Dark`;
  return (
    <IconTooltip label={appearance === "light" ? t`Switch to dark` : t`Switch to light`}>
      <Button
        variant="floating"
        data-testid="appearance-toggle"
        aria-label={appearance === "light" ? t`Switch to dark` : t`Switch to light`}
        onClick={() => setAppearance(setUiAppearance(appearance === "light" ? "dark" : "light"))}
        className="app-no-drag hidden h-10 gap-1.5 rounded-full px-3 text-[13px] font-normal text-muted-foreground md:inline-flex"
      >
        {appearance === "light" ? (
          <Sun strokeWidth={1.6} className="size-[15px]" aria-hidden="true" />
        ) : (
          <Moon strokeWidth={1.6} className="size-[15px]" aria-hidden="true" />
        )}
        <span aria-hidden="true">{label}</span>
      </Button>
    </IconTooltip>
  );
}

/**
 * The header over the transcript: navigation, the chat's name and state, appearance and computer. Phones float
 * the name as a centred pill; wider screens set it at the start of a frosted bar.
 */
export function ChatHeader({
  inGroup,
  active,
  activeGroup,
  activeSnapshot,
  tools = [],
  computerPanelOpen,
  detailsPanelOpen = false,
  onOpenNavigation,
  onOpenChatSettings,
  onToggleComputer,
  onToggleDetails,
  trialDaysLeft = null,
  onOpenBilling,
}: {
  inGroup: boolean;
  active: Bot | undefined;
  activeGroup: Group | undefined;
  activeSnapshot: ThreadSnapshot | null;
  /** Names of the tools the bot can use, listed after its state. */
  tools?: readonly string[];
  computerPanelOpen: boolean;
  /** Details or Library is open beside the chat. */
  detailsPanelOpen?: boolean;
  onOpenNavigation: () => void;
  onOpenChatSettings: () => void;
  onToggleComputer: (bot: Bot) => void;
  onToggleDetails?: () => void;
  /** Days left in the organization's trial; null hides the pill. */
  trialDaysLeft?: number | null;
  onOpenBilling?: () => void;
}) {
  const { t } = useLingui();
  const state = chatHeaderState(active?.status ?? "idle");
  const stateLabels = { idle: t`Idle`, working: t`Working`, "needs-you": t`Needs you` };
  return (
    <div className="pointer-events-none absolute inset-x-0 top-0 z-10 grid grid-cols-[1fr_minmax(0,auto)_1fr] items-center gap-2 px-3 py-[13px] md:flex md:bg-card md:px-5 md:py-3 md:backdrop-blur-md [&_button]:pointer-events-auto">
      <div className="flex items-center md:hidden">
        <Button
          variant="floating"
          size="icon-lg"
          aria-label={t`Open navigation`}
          onClick={onOpenNavigation}
          className="app-no-drag md:hidden"
        >
          <Menu strokeWidth={1.7} className="size-[19px]" />
        </Button>
      </div>
      <Button
        variant="floating"
        data-testid="bot-settings-trigger"
        onClick={onOpenChatSettings}
        className="app-no-drag h-auto min-w-0 shrink gap-2 py-1 ps-1 pe-3.5 md:gap-3 md:border-transparent md:bg-transparent md:ps-1.5 md:backdrop-blur-none"
      >
        {inGroup ? (
          <GroupAvatar members={activeSnapshot?.members ?? activeGroup?.members ?? []} size={32} />
        ) : active ? (
          <BotAvatar
            avatarSet={active.avatarSet}
            color={active.color}
            identity={active.id}
            size={32}
            status={active.status}
            starLabel={active.superBot ? t`Super Nexa Mate` : undefined}
          />
        ) : null}
        <span className="min-w-0 text-start">
          <span className="flex min-w-0 items-center gap-2">
            <span
              className="block truncate text-[15px] font-medium text-foreground md:text-[16px] md:font-semibold"
              dir="auto"
            >
              {inGroup
                ? (activeGroup?.name ?? activeSnapshot?.groupName ?? t`Group`)
                : (active?.name ?? t`Select a Nexa Mate`)}
            </span>
            {!inGroup && active?.title?.trim() ? (
              <Badge
                data-testid="chat-header-title"
                variant="secondary"
                className="hidden max-w-[14rem] truncate bg-code text-primary-strong md:inline-flex"
                dir="auto"
              >
                {active.title.trim()}
              </Badge>
            ) : null}
          </span>
          {!inGroup && active ? (
            <span
              data-testid="chat-header-status"
              data-state={state}
              className="hidden max-w-full items-center gap-1.5 text-[12.5px] font-normal text-muted-foreground md:flex"
            >
              <span
                aria-hidden="true"
                className={`size-1.5 shrink-0 rounded-full ${
                  state === "working"
                    ? "bg-status-working"
                    : state === "needs-you"
                      ? "bg-warning"
                      : "bg-faint-foreground"
                }`}
              />
              <span className="truncate">
                {[stateLabels[state], tools.join(", ")].filter(Boolean).join(" · ")}
              </span>
            </span>
          ) : null}
        </span>
        {/* The whole Space reads a shared bot's chat, so say so where people type into it. */}
        {!inGroup && active?.visibility === "space" ? (
          <span data-testid="shared-bot-indicator" className="flex shrink-0 items-center">
            <Users aria-hidden strokeWidth={1.7} className="size-[15px] text-muted-foreground" />
            <span className="sr-only">{t`Shared with Space`}</span>
          </span>
        ) : null}
      </Button>
      <div className="flex items-center justify-self-end gap-2 md:ms-auto">
        {trialDaysLeft !== null && onOpenBilling ? (
          <Button
            variant="floating"
            data-testid="trial-pill"
            onClick={onOpenBilling}
            className="app-no-drag hidden h-10 rounded-full px-3 text-[12px] font-normal text-muted-foreground md:inline-flex"
          >
            <Plural value={trialDaysLeft} one="Trial · # day left" other="Trial · # days left" />
          </Button>
        ) : null}
        <AppearanceToggle />
        {!inGroup && active ? (
          <IconTooltip label={t`Agent computer`}>
            <Button
              variant="floating"
              size="icon-lg"
              aria-label={t`Agent computer`}
              aria-pressed={computerPanelOpen}
              onClick={() => onToggleComputer(active)}
              className="app-no-drag"
            >
              <Monitor strokeWidth={1.6} className="size-[18px]" />
            </Button>
          </IconTooltip>
        ) : null}
        {!inGroup && active && onToggleDetails ? (
          <IconTooltip label={t`Details`}>
            <Button
              variant="floating"
              size="icon-lg"
              aria-label={t`Details`}
              aria-pressed={detailsPanelOpen}
              data-testid="details-panel-toggle"
              onClick={onToggleDetails}
              className="app-no-drag"
            >
              <PanelRight strokeWidth={1.6} className="size-[18px]" />
            </Button>
          </IconTooltip>
        ) : null}
      </div>
    </div>
  );
}
